'use client';

import { ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';
import { AutocompleteOptionList } from './autocomplete-option-list';
import { Input } from './input';
import { Label } from './label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './sheet';
import type { AutocompleteViewProps } from './use-autocomplete';

/**
 * Mobile rendering for `Autocomplete`: a single trigger button plus a bottom
 * sheet with the search input and option list. Extracted from `autocomplete.tsx`
 * (conformance, code-size guard).
 */
export function AutocompleteMobile({ props, state }: AutocompleteViewProps) {
  const {
    multiple,
    options,
    className,
    label,
    required,
    disabled,
    inputClassName,
    error,
    placeholder,
    hint,
    trailingAction,
    loading,
    loadingMessage,
    header,
    maxSelections,
  } = props;
  const ariaLabel = props['aria-label'];
  const {
    selectedValues,
    atLimit,
    displayValue,
    labelId,
    inputId,
    isOpen,
    setIsOpen,
    listboxId,
    handleClear,
    handleSelect,
    inputRef,
    inputValue,
    handleInputChange,
    listRef,
    statusId,
    filteredOptions,
  } = state;

  // Multi-select surfaces selections as a rich removable chip row BELOW the
  // trigger (owned by the primitive — wrappers must not add their own strip),
  // mirroring the desktop in-field chips. The trigger itself then just shows
  // the placeholder. Single-select keeps its collapsed label in the trigger.
  const mobileTriggerText = multiple ? '' : displayValue;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label id={labelId} htmlFor={inputId}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {/* Trigger button for mobile */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        disabled={disabled}
        className={cn(
          'border-input bg-background ring-offset-background flex h-11 w-full items-center justify-between rounded-md border px-3 py-2 text-left',
          'focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-hidden',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive',
          inputClassName,
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
      >
        <span
          className={cn(
            'truncate',
            !mobileTriggerText && 'text-muted-foreground',
          )}
        >
          {mobileTriggerText || placeholder}
        </span>
        <div className="flex items-center gap-1">
          {selectedValues.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="hover:bg-muted rounded-sm p-1"
              aria-label="Clear selection"
            >
              <Cross2Icon className="h-4 w-4" />
            </button>
          )}
          <ChevronDownIcon className="text-muted-foreground h-4 w-4" />
        </div>
      </button>

      {hint && !error && (
        <p className="text-muted-foreground text-sm">{hint}</p>
      )}
      {error && <p className="text-destructive text-sm">{error}</p>}

      {/* Selected chips (multi) — primitive-owned; wrappers add no strip. */}
      {multiple && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedValues.map((v) => {
            const opt = options.find((o) => o.value === v);
            const chipLabel = opt?.label ?? v;
            return (
              <span
                key={v}
                className="bg-muted text-foreground inline-flex max-w-full items-center gap-1 rounded-sm py-0.5 pr-1 pl-1.5 text-sm"
              >
                {opt?.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                <span className="max-w-[10rem] truncate">{chipLabel}</span>
                <button
                  type="button"
                  onClick={() => handleSelect(v)}
                  aria-label={`Remove ${chipLabel}`}
                  className="hover:bg-foreground/10 text-muted-foreground hover:text-foreground rounded-sm p-0.5 transition-colors"
                  disabled={disabled}
                >
                  <Cross2Icon className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Selection cap counter */}
      {multiple && maxSelections ? (
        <p
          className={cn(
            'text-xs',
            atLimit ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {selectedValues.length} / {maxSelections}
        </p>
      ) : null}

      {/* Mobile Sheet */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] px-0">
          <SheetHeader className="px-4 pb-2">
            <SheetTitle>{label || placeholder}</SheetTitle>
          </SheetHeader>

          {header ? (
            <div className="text-muted-foreground px-4 pb-2 text-sm">
              {header}
            </div>
          ) : null}

          {/* Search input in sheet */}
          <div className="border-b px-4 pb-3">
            <div className="relative">
              <Input
                ref={inputRef}
                type="text"
                role="searchbox"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={placeholder}
                className={cn(
                  'h-11 text-base', // text-base prevents iOS zoom
                  trailingAction ? 'pr-16' : 'pr-10',
                )}
                autoFocus
              />
              <div className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-1">
                {trailingAction}
                {loading && (
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                )}
              </div>
            </div>
          </div>

          {/* Options list */}
          <div
            ref={listRef}
            role="listbox"
            aria-label={label || ariaLabel}
            className="max-h-[60vh] overflow-y-auto"
          >
            <AutocompleteOptionList props={props} state={state} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Screen reader announcements */}
      <div
        id={statusId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {loading
          ? loadingMessage
          : `${filteredOptions.length} results available`}
      </div>
    </div>
  );
}
